/**
 * Care Background Agent · frontend/src/App.tsx
 * Root app with Auth0 authentication, session list, and session view.
 * Addresses: #09 Auth (Auth0), #22 Frontend Deployment, P5.05 Session Search
 */

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { BrowserRouter, Routes, Route, Link, useParams } from "react-router-dom";
import ChatPanel from "./components/ChatPanel";
import CodeServerFrame from "./components/CodeServerFrame";
import BrowserStream from "./components/BrowserStream";
import SessionList from "./components/SessionList";
import HandoverReport from "./components/HandoverReport";
import { useSession } from "./hooks/useSession";

const AUTH0_DOMAIN = import.meta.env.VITE_AUTH0_DOMAIN ?? "";
const AUTH0_CLIENT_ID = import.meta.env.VITE_AUTH0_CLIENT_ID ?? "";

function LoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center bg-[#0f1117] text-[#8b8fa8]">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[#5b4dff] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm">Loading…</p>
      </div>
    </div>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    loginWithRedirect();
    return null;
  }
  return <>{children}</>;
}

function SessionView() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { session, messages, liveEvents, loading, error } = useSession(sessionId!);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0f1117] text-[#8b8fa8]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#5b4dff] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm">Loading session…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0f1117] text-red-400">
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#0f1117] overflow-hidden">
      <header className="flex items-center justify-between px-4 py-2 bg-[#181c2a] border-b border-[#2a2e45] shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/" className="text-[#8b8fa8] hover:text-white text-sm transition-colors">
            ← Sessions
          </Link>
          <div className="h-4 w-px bg-[#2a2e45]" />
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-[#5b4dff] to-[#8b5cf6] flex items-center justify-center text-white text-xs font-bold">
              C
            </div>
            <span className="text-white font-semibold text-sm">Care</span>
          </div>
          {session && (
            <span className="text-[#8b8fa8] text-xs font-mono truncate max-w-xs">
              {session.task_title ?? session.id}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {session && <StatusBadge status={session.status} />}
          <UserButton />
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <div className="w-[340px] shrink-0 border-r border-[#2a2e45] flex flex-col min-h-0">
          <ChatPanel messages={messages} liveEvents={liveEvents} session={session} />
        </div>

        <div className="flex-1 min-w-0 border-r border-[#2a2e45] flex flex-col min-h-0">
          <PanelHeader label="VS Code" icon="⌨️" />
          <div className="flex-1 min-h-0">
            <CodeServerFrame session={session} />
          </div>
        </div>

        <div className="w-[380px] shrink-0 flex flex-col min-h-0">
          <PanelHeader label="Browser" icon="🌐" />
          <div className="flex-1 min-h-0">
            <BrowserStream session={session} />
          </div>
          {session?.handover_report && (
            <div className="border-t border-[#2a2e45] max-h-72 overflow-y-auto">
              <HandoverReport report={session.handover_report} summaryJson={session.summary_json} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active:    "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    booting:   "bg-blue-500/20 text-blue-400 border-blue-500/30",
    pending:   "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    suspended: "bg-gray-500/20 text-gray-400 border-gray-500/30",
    completed: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    failed:    "bg-red-500/20 text-red-400 border-red-500/30",
  };
  const pulses = new Set(["active", "booting"]);
  const cls = styles[status] ?? "bg-gray-500/20 text-gray-400 border-gray-500/30";

  return (
    <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs ${cls}`}>
      {pulses.has(status) && (
        <span className="relative flex h-1.5 w-1.5">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${status === "active" ? "bg-emerald-400" : "bg-blue-400"}`} />
          <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${status === "active" ? "bg-emerald-400" : "bg-blue-400"}`} />
        </span>
      )}
      {status}
    </div>
  );
}

function PanelHeader({ label, icon }: { label: string; icon: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-[#181c2a] border-b border-[#2a2e45] shrink-0">
      <span>{icon}</span>
      <span className="text-xs text-[#8b8fa8] font-medium">{label}</span>
    </div>
  );
}

function UserButton() {
  const { user } = useAuth0();
  return user ? (
    <div className="flex items-center gap-2">
      {user.picture && (
        <img src={user.picture} alt={user.name ?? ""} className="w-6 h-6 rounded-full" />
      )}
      <span className="text-xs text-[#8b8fa8] hidden sm:block">{user.email}</span>
    </div>
  ) : null;
}

export default function App() {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{ redirect_uri: window.location.origin }}
    >
      <BrowserRouter>
        <AuthGuard>
          <Routes>
            <Route path="/" element={<SessionList />} />
            <Route path="/session/:sessionId" element={<SessionView />} />
          </Routes>
        </AuthGuard>
      </BrowserRouter>
    </Auth0Provider>
  );
}
